/**
 * Catches errors from a promise and returns a tuple with either the error or the resolved value.
 *
 * @template T - The type of the resolved value of the promise.
 * @template E - The type of the errors to catch, extending the Error constructor.
 * 
 * @param {Promise<T>} promise - The promise to handle.
 * @param {E[]} [errorsToCatch] - An optional array of error constructors to catch. If not provided, all errors will be caught.
 * 
 * @returns {Promise<[undefined, T] | [InstanceType<E>]>} A promise that resolves to a tuple. 
 * If the promise resolves successfully, the tuple will be [undefined, T]. 
 * If an error is caught, the tuple will be [InstanceType<E>].
 */
export async function catchErrorAsync<T, E extends new (message?: string) => Error>(
	promise: Promise<T>,
	errorsToCatch?: E[]
): Promise<[undefined, T] | [InstanceType<E>]> {
	return promise
		.then( data => [undefined, data] as [undefined, T])
		.catch(error => {
			if (errorsToCatch === undefined) {
				return [error];
			}

			if (errorsToCatch.some(e => error instanceof e )) {
				return [error];
			}

			throw error;
		});
}